import { all, contains, equals, isEmpty, values } from "ramda";
import type { DefaultRootState } from "react-redux";
import type { Dispatch } from "redux";
import type { ThunkAction } from "redux-thunk";
import { logout } from "interfaces/authentication";
import { confirmUser } from "interfaces/user";

import { RequestStatus } from "types/request";
import type { IUser } from "types/user";

export type UserActionTypes =
  | IReceiveUserAction
  | IRequestUserAction
  | IConfirmingUserAction
  | IConfirmedUserAction
  | IOtherAction;

export type ThunkResult<R> = ThunkAction<
  R,
  DefaultRootState,
  undefined,
  UserActionTypes
>;

export enum TypeKeys {
  REQUEST_USER = "USER/REQUEST_USER",
  RECEIVE_USER = "USER/RECEIVE_USER",
  CONFIRMING_USER = "USER/CONFIRMING_USER",
  CONFIRMED_USER = "USER/CONFIRMED_USER",
  OTHER_ACTION = "USER/__any_other_action_type__",
}

export interface IReceiveUserAction {
  type: TypeKeys.RECEIVE_USER;
  user: IUser;
  userRequestStatus: RequestStatus;
}

export const receiveUser = (user: IUser): IReceiveUserAction => ({
  type: TypeKeys.RECEIVE_USER,
  user,
  userRequestStatus: RequestStatus.SUCCESS,
});

export interface IRequestUserAction {
  type: TypeKeys.REQUEST_USER;
  userRequestStatus: RequestStatus;
}

export const requestUser = (): IRequestUserAction => ({
  type: TypeKeys.REQUEST_USER,
  userRequestStatus: RequestStatus.REQUESTING,
});

export interface IConfirmingUserAction {
  type: TypeKeys.CONFIRMING_USER;
  userConfirmationRequestStatus: RequestStatus;
}

export interface IConfirmedUserAction {
  type: TypeKeys.CONFIRMED_USER;
  message?: string;
  userConfirmationRequestStatus: RequestStatus;
}

const confirmingUser = (): IConfirmingUserAction => ({
  type: TypeKeys.CONFIRMING_USER,
  userConfirmationRequestStatus: RequestStatus.REQUESTING,
});

const confirmedUser = (
  status: RequestStatus,
  message?: string
): IConfirmedUserAction => ({
  type: TypeKeys.CONFIRMED_USER,
  message,
  userConfirmationRequestStatus: status,
});

export const confirmationRequest = (
  token: string,
  password: string
): ThunkResult<void> => {
  return (dispatch: Dispatch<any>): Promise<void> => {
    dispatch(confirmingUser());
    return confirmUser(token, password)
      .then((responses) => {
        const respVals = values(responses);
        if (!isEmpty(respVals)) {
          if (
            all(equals("success"), respVals) ||
            (contains("no token", respVals) && contains("success", respVals))
          ) {
            dispatch(confirmedUser(RequestStatus.SUCCESS));
          } else if (
            all(equals("failed"), respVals) ||
            (contains("no token", respVals) && contains("failed", respVals))
          ) {
            dispatch(confirmedUser(RequestStatus.FAILURE));
          } else {
            dispatch(confirmedUser(RequestStatus.FAILURE, responses));
          }
        } else {
          dispatch(confirmedUser(RequestStatus.FAILURE));
        }
      })
      .catch((error) => {
        dispatch(confirmedUser(RequestStatus.FAILURE, error));
      });
  };
};

export const triggerLogout = (): ThunkResult<void> => {
  return (): void => {
    return logout();
  };
};

export interface IOtherAction {
  type: TypeKeys.OTHER_ACTION;
}
